import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { BarChart3, CircleHelp, ClipboardList, Download, Eye, RefreshCw, Send, Settings, Trash2, Users, FileText, Sprout, Warehouse, TriangleAlert } from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";
import { accountingApi } from "@/features/accounting/services/accountingApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getBusinessDateToday, isBusinessDateToday } from "@/lib/businessDate";
import {
  DirectorsReportDeliveryType,
  DirectorsReportFrequency,
  DirectorsReportPeriodRule,
  DirectorsReportSection,
  CompanyPerformanceMetricAvailability,
  directorsReportsApi,
  type CompanyPerformanceMetricDto,
  type CompanyPerformancePeriodValueDto,
  type SeedlingsMovementReportDto,
  type SeedlingsMovementRowDto,
  type DirectorsReportDetailDto,
  type DirectorsReportRecipientDto,
  type DirectorsReportScheduleDto,
  type DirectorsReportSettingsDto,
  type DirectorsReportSummaryDto,
  type SalesPerformanceReportDto,
} from "../services/directorsReportsApi";

const sectionOptions = [
  [DirectorsReportSection.ExecutiveSummary, "Executive summary"],
  [DirectorsReportSection.SalesPerformance, "Sales section"],
  [DirectorsReportSection.OvergrownSeedlings, "Overgrown section"],
  [DirectorsReportSection.SalesTrendGraph, "Sales graph"],
  [DirectorsReportSection.OvergrownTrendGraph, "Overgrown graph"],
  [DirectorsReportSection.CompanyPerformance, "Sowing & operations report"],
] as const;

const supportedSections = new Set<DirectorsReportSection>(sectionOptions.map(([section]) => section));
const sanitizeSections = (sections: DirectorsReportSection[]) => sections.filter((section) => supportedSections.has(section));
const recipientEmailSplitPattern = /[,;\n]+/;

function splitRecipientEmails(value: string) {
  const seen = new Set<string>();
  return value
    .split(recipientEmailSplitPattern)
    .map((email) => email.trim().toLowerCase())
    .filter((email) => {
      if (!email || seen.has(email)) return false;
      seen.add(email);
      return true;
    });
}

function isValidRecipientEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const dayOptions = [
  [1, "Monday"],
  [2, "Tuesday"],
  [3, "Wednesday"],
  [4, "Thursday"],
  [5, "Friday"],
  [6, "Saturday"],
  [0, "Sunday"],
] as const;

export function DirectorsReportsPage() {
  const qc = useQueryClient();
  const companiesQuery = useQuery({ queryKey: ["companies"], queryFn: accountingApi.getCompanies });
  const companyId = companiesQuery.data?.[0]?.id ?? "";

  const settingsQuery = useQuery({
    queryKey: ["directors-report-settings", companyId],
    queryFn: () => directorsReportsApi.getSettings(companyId),
    enabled: !!companyId,
  });
  const schedulesQuery = useQuery({
    queryKey: ["directors-report-schedules", companyId],
    queryFn: () => directorsReportsApi.getSchedules(companyId),
    enabled: !!companyId,
  });
  const recipientsQuery = useQuery({
    queryKey: ["directors-report-recipients", companyId],
    queryFn: () => directorsReportsApi.getRecipients(companyId),
    enabled: !!companyId,
  });
  const reportsQuery = useQuery({
    queryKey: ["directors-reports", companyId],
    queryFn: () => directorsReportsApi.getReports(companyId),
    enabled: !!companyId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["directors-report-settings", companyId] });
    qc.invalidateQueries({ queryKey: ["directors-report-schedules", companyId] });
    qc.invalidateQueries({ queryKey: ["directors-report-recipients", companyId] });
    qc.invalidateQueries({ queryKey: ["directors-reports", companyId] });
  };

  if (!companyId) {
    return <div className="p-6 text-sm text-muted-foreground">Create a company before configuring directors reports.</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal">Directors Reports</h1>
          <p className="text-sm text-muted-foreground">{companiesQuery.data?.[0]?.companyName ?? "Company"}</p>
        </div>
        <Button variant="outline" size="icon" onClick={invalidate} aria-label="Refresh directors reports">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <Tabs defaultValue="history">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="history"><FileText className="mr-2 h-4 w-4" />History</TabsTrigger>
          <TabsTrigger value="performance"><BarChart3 className="mr-2 h-4 w-4" />Sowing & Operations</TabsTrigger>
          <TabsTrigger value="seedlings-audit"><ClipboardList className="mr-2 h-4 w-4" />Seedlings Movement</TabsTrigger>
          <TabsTrigger value="settings"><Settings className="mr-2 h-4 w-4" />Settings</TabsTrigger>
          <TabsTrigger value="recipients"><Users className="mr-2 h-4 w-4" />Recipients</TabsTrigger>
          <TabsTrigger value="generate"><Send className="mr-2 h-4 w-4" />Generate</TabsTrigger>
        </TabsList>

        <TabsContent value="history">
          <History
            companyId={companyId}
            reports={reportsQuery.data ?? []}
            schedules={schedulesQuery.data ?? []}
            onChanged={invalidate}
          />
        </TabsContent>
        <TabsContent value="performance">
          <CompanyPerformancePanel
            companyId={companyId}
            companyName={companiesQuery.data?.[0]?.companyName ?? "FarmSmart"}
            schedules={schedulesQuery.data ?? []}
            onChanged={invalidate}
          />
        </TabsContent>
        <TabsContent value="seedlings-audit">
          <SeedlingsMovementPanel
            companyId={companyId}
            schedules={schedulesQuery.data ?? []}
            onChanged={invalidate}
          />
        </TabsContent>
        <TabsContent value="settings">
          {settingsQuery.data && (
            <SettingsPanel companyId={companyId} settings={settingsQuery.data} schedules={schedulesQuery.data ?? []} onChanged={invalidate} />
          )}
        </TabsContent>
        <TabsContent value="recipients">
          <RecipientsPanel companyId={companyId} schedules={schedulesQuery.data ?? []} recipients={recipientsQuery.data ?? []} onChanged={invalidate} />
        </TabsContent>
        <TabsContent value="generate">
          <GeneratePanel
            companyId={companyId}
            businessTimezone={settingsQuery.data?.businessTimezone ?? "Africa/Nairobi"}
            schedules={schedulesQuery.data ?? []}
            onChanged={invalidate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CompanyPerformancePanel({
  companyId,
  companyName,
  schedules,
  onChanged,
}: {
  companyId: string;
  companyName: string;
  schedules: DirectorsReportScheduleDto[];
  onChanged: () => void;
}) {
  const [asOf, setAsOf] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [recipientScheduleId, setRecipientScheduleId] = useState("");
  const actionLock = useRef(false);
  const reportQuery = useQuery({
    queryKey: ["company-performance", companyId, asOf],
    queryFn: () => directorsReportsApi.getCompanyPerformance(companyId, asOf),
    enabled: !!companyId && !!asOf,
  });
  const salesQuery = useQuery({
    queryKey: ["sales-performance", companyId, asOf],
    queryFn: () => directorsReportsApi.getSalesPerformance(companyId, asOf),
    enabled: !!companyId && !!asOf,
  });
  const exportReport = useMutation({
    mutationFn: async (action: "download" | "email") => {
      if (actionLock.current) return null;
      actionLock.current = true;
      try {
        const periodStart = new Date(`${asOf}T00:00:00Z`);
        const periodEnd = new Date(periodStart);
        periodEnd.setUTCDate(periodEnd.getUTCDate() + 1);
        const generated = await directorsReportsApi.generate({
          companyId,
          reportType: DirectorsReportFrequency.Custom,
          periodStart: periodStart.toISOString(),
          periodEnd: periodEnd.toISOString(),
          enabledSections: [DirectorsReportSection.CompanyPerformance],
          compareWithPreviousPeriod: true,
          deliverEmail: false,
          asOf,
        }, crypto.randomUUID());

        if (action === "download") {
          await directorsReportsApi.download(generated.id);
          return { action, message: "Sowing & operations PDF downloaded" };
        }

        const delivery = await directorsReportsApi.send(generated.id, recipientScheduleId);
        return { action, message: delivery.message, delivery };
      } finally {
        actionLock.current = false;
      }
    },
    onSuccess: (result) => {
      if (!result) return;
      if (result.action === "download") toast.success(result.message);
      else if (result.delivery?.status === "Delivered") toast.success(result.message);
      else if (result.delivery?.failedCount) toast.error(result.message);
      else toast.warning(result.message);
      onChanged();
    },
    onError: () => toast.error("The sowing & operations report could not be exported or sent."),
  });

  return (
    <Card>
      <CardHeader className="gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <CardTitle>Sowing & Operations Report</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Sowings, greenhouse transfers, overgrowns, dispatches and sales using the same reporting periods.
          </p>
        </div>
        <div className="grid w-full gap-3 sm:grid-cols-[180px_minmax(220px,1fr)_auto_auto] xl:max-w-3xl">
          <div className="space-y-1.5">
            <Label htmlFor="company-performance-as-of">As of</Label>
            <Input
              id="company-performance-as-of"
              type="date"
              value={asOf}
              onChange={(event) => setAsOf(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Email recipient group</Label>
            <Select value={recipientScheduleId} onValueChange={setRecipientScheduleId}>
              <SelectTrigger><SelectValue placeholder="Select a schedule" /></SelectTrigger>
              <SelectContent>
                {schedules.map((schedule) => (
                  <SelectItem key={schedule.id} value={schedule.id}>{schedule.scheduleName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            className="self-end"
            disabled={exportReport.isPending || !reportQuery.data || !salesQuery.data}
            onClick={() => exportReport.mutate("download")}
          >
            <Download className="mr-2 h-4 w-4" />Export PDF
          </Button>
          <Button
            className="self-end"
            disabled={exportReport.isPending || !reportQuery.data || !salesQuery.data || !recipientScheduleId}
            onClick={() => exportReport.mutate("email")}
          >
            <Send className="mr-2 h-4 w-4" />{exportReport.isPending ? "Working…" : "Email report"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {(reportQuery.isLoading || salesQuery.isLoading) && <p className="py-8 text-center text-sm text-muted-foreground">Loading directors dashboard…</p>}
        {(reportQuery.isError || salesQuery.isError) && (
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-destructive">
            Could not load the sowing & operations report.
            <Button variant="outline" size="sm" onClick={() => { reportQuery.refetch(); salesQuery.refetch(); }}>Retry</Button>
          </div>
        )}
        {reportQuery.data && salesQuery.data && (
          <>
            <DirectorsExecutiveDashboard
              companyName={companyName}
              performance={reportQuery.data}
              sales={salesQuery.data}
            />
            <details className="mt-4 rounded-md border bg-background/80 p-3">
              <summary className="cursor-pointer text-sm font-medium">Metric details and period audit</summary>
              <div className="mt-3 overflow-x-auto rounded-md border">
              <Table className="min-w-[900px] table-fixed">
                <TableHeader>
                  <TableRow className="bg-muted/70">
                    <TableHead className="w-[22%]">Metric</TableHead>
                    <TableHead className="w-[15%] text-right">{reportQuery.data.periodLabels.daily}</TableHead>
                    <TableHead className="w-[15%] text-right">{reportQuery.data.periodLabels.weekly}</TableHead>
                    <TableHead className="w-[15%] text-right">{reportQuery.data.periodLabels.monthly}</TableHead>
                    <TableHead className="w-[18%] text-right">{reportQuery.data.periodLabels.yearToDate}</TableHead>
                    <TableHead className="w-[15%]">
                      <div className="flex items-center justify-end gap-1.5">
                        <span>Trend · {reportQuery.data.periodLabels.monthlyTrend}</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                              aria-label="Explain monthly trends"
                            >
                              <CircleHelp className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-sm text-left leading-relaxed">
                            Compares month-to-date through the selected date with the same elapsed portion of the previous month.
                            ↑ means higher, ↓ means lower, and → means effectively unchanged. The arrow describes change—it does not mean good or bad.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportQuery.data.metrics.map((metric) => (
                    <CompanyPerformanceRow
                      key={metric.code}
                      metric={metric}
                      currency={reportQuery.data.currency}
                    />
                  ))}
                </TableBody>
              </Table>
              </div>
            </details>
            {reportQuery.data.dataQualityWarnings.length > 0 && (
              <div className="mt-4 rounded-md border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-950">
                <p className="font-medium">Data notes</p>
                <ul className="mt-1 list-disc space-y-1 pl-4">
                  {reportQuery.data.dataQualityWarnings.map((warning) => <li key={warning}>{warning}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

const dashboardGreens = ["#1f5f3b", "#438b45", "#75ad4f", "#a7c96a", "#d4df9b"];
const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function DirectorsExecutiveDashboard({
  companyName,
  performance,
  sales,
}: {
  companyName: string;
  performance: import("../services/directorsReportsApi").CompanyPerformanceReportDto;
  sales: SalesPerformanceReportDto;
}) {
  const isLive = isBusinessDateToday(sales.asOf, performance.businessTimezone);
  const metric = (code: string) => performance.metrics.find((item) => item.code === code);
  const sowings = metric("sowings");
  const salesTrays = metric("sales-trays");
  const recognizedRevenue = metric("recognized-revenue");
  const cashReceived = metric("cash-received");
  const outstandingPayments = metric("outstanding-payments");
  const transfers = metric("greenhouse-transfers");
  const overgrown = metric("overgrown");
  const gappings = metric("gappings");
  const dispatches = metric("dispatches");
  const products = sales.breakdown.daily.crops
    .flatMap((crop) => crop.varieties.map((variety) => ({ ...variety, cropName: crop.cropName })))
    .sort((a, b) => b.grossSalesAmount - a.grossSalesAmount)
    .slice(0, 8);
  const largestProductValue = Math.max(...products.map((item) => item.grossSalesAmount), 1);
  const yearSeries = sales.yearComparison.map((point) => ({
    month: monthLabels[point.month - 1],
    current: point.currentYearAmount,
    previous: point.previousYearAmount,
  }));
  const currentYear = Number(sales.asOf.slice(0, 4));
  const dispatchBreakdown = dispatches?.dailyBreakdown ?? [];

  return (
    <section className="overflow-hidden rounded-xl border border-[#b8c49d] bg-[#f2f1e4] shadow-sm print:shadow-none">
      <header className="bg-gradient-to-r from-[#244d2f] via-[#3f7236] to-[#668b3b] px-6 py-5 text-center text-white">
        <h2 className="text-2xl font-semibold tracking-wide sm:text-3xl">{companyName}</h2>
        <div className="mt-1 flex flex-wrap justify-center gap-x-4 text-xs text-white/80">
          <span>Directors performance dashboard</span>
          <span>As of {sales.asOf}</span>
          <span>{performance.businessTimezone}</span>
        </div>
        <Badge variant="outline" className="mt-3 border-white/30 bg-white/10 text-white">
          {isLive ? "Live today - updates as activity is recorded" : "Historical date"}
        </Badge>
      </header>

      <div className="grid gap-2 border-b border-[#c6ceb5] bg-[#e8eadb] p-3 sm:grid-cols-2 xl:grid-cols-4">
        <ExecutiveMetricCard
          label="Sales - No. of Trays"
          value={salesTrays?.daily.trayEquivalent == null ? "—" : formatTrays(salesTrays.daily.trayEquivalent)}
          period={performance.periodLabels.daily}
        />
        <ExecutiveMetricCard
          label="Recognized Revenue"
          value={recognizedRevenue?.daily.monetaryValue == null ? "—" : formatKsh(recognizedRevenue.daily.monetaryValue)}
          period={performance.periodLabels.daily}
        />
        <ExecutiveMetricCard
          label="Posted Cash Received"
          value={cashReceived?.daily.monetaryValue == null ? "—" : formatKsh(cashReceived.daily.monetaryValue)}
          period={performance.periodLabels.daily}
        />
        <ExecutiveMetricCard
          label="Posted Trade Receivables"
          value={outstandingPayments?.daily.monetaryValue == null ? "—" : formatKsh(outstandingPayments.daily.monetaryValue)}
          period={`As of ${sales.asOf}`}
        />
      </div>

      <div className="grid gap-3 p-3 lg:grid-cols-12">
        <DashboardPanel title="Daily Sales Report" className="lg:col-span-3 lg:row-span-2">
          <div className="space-y-3">
            {products.length === 0 && <EmptyMetric text="No eligible sales recorded today" />}
            {products.map((product) => (
              <div key={`${product.cropName}-${product.varietyName}`}>
                <div className="mb-1 flex items-end justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate font-medium" title={`${product.cropName} — ${product.varietyName}`}>
                    {product.cropName} — {product.varietyName}
                  </span>
                  <span className="shrink-0 tabular-nums">{formatTrays(product.trayEquivalent)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[#dce3ca]">
                  <div className="h-full rounded-full bg-[#4b8a39]" style={{ width: `${Math.max(5, product.grossSalesAmount / largestProductValue * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 border-t border-[#bcc7aa] pt-4">
            <KpiLine label="No. of Trays" value={numberFormatter.format(sales.daily.trayEquivalent)} />
            <KpiLine label="Sales (invoiced)" value={formatKsh(sales.daily.grossSalesAmount)} accent />
          </div>
        </DashboardPanel>

        <DashboardPanel title="Weekly & Monthly Sales" className="lg:col-span-5">
          <div className="grid gap-3 sm:grid-cols-2 sm:divide-x sm:divide-[#c7ceb7]">
            <SalesPeriodChart title={sales.periodLabels.weekly} metrics={sales.weekly} data={sales.weeklyDailySeries} />
            <SalesPeriodChart title={sales.periodLabels.monthly} metrics={sales.monthly} data={sales.monthlyDailySeries} padded />
          </div>
        </DashboardPanel>

        <DashboardPanel title="Month-to-Month Comparison" className="lg:col-span-4">
          <div className="mb-2 flex justify-end gap-4 text-xs">
            <LegendDot color="#2e6835" label={String(currentYear)} />
            <LegendDot color="#8db548" label={String(currentYear - 1)} />
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={yearSeries} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#d9ddca" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={compactMoney} />
                <ChartTooltip formatter={(value) => formatKsh(Number(value))} />
                <Line type="monotone" dataKey="previous" connectNulls={false} stroke="#8db548" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="current" connectNulls={false} stroke="#2e6835" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </DashboardPanel>

        <DashboardPanel title="Seedlings Transfers" className="lg:col-span-3">
          <div className="flex items-center gap-4">
            <Warehouse className="h-14 w-14 text-[#4a783b]" strokeWidth={1.4} />
            <div>
              <p className="text-xs text-muted-foreground">Acknowledged into greenhouse today</p>
              <p className="text-xl font-bold text-[#245c32]">{formatPeriodQuantity(transfers?.daily)}</p>
            </div>
          </div>
          <DataGapLine label="Transfer value" text="Not valued in the current domain model" />
        </DashboardPanel>

        <DashboardPanel title="Overgrowns & Losses" className="lg:col-span-2">
          <div className="flex items-center gap-3">
            <TriangleAlert className="h-11 w-11 text-[#a15c35]" strokeWidth={1.5} />
            <div>
              <p className="text-xs text-muted-foreground">Became overgrown today</p>
              <p className="text-lg font-bold text-[#8b4b2d]">{formatPeriodQuantity(overgrown?.daily)}</p>
            </div>
          </div>
          <DataGapLine label="Financial loss" text="Cost valuation is not captured" />
        </DashboardPanel>

        <DashboardPanel title="Today's Dispatches" className="lg:col-span-4 lg:row-span-2">
          <div className="grid items-center gap-3 sm:grid-cols-[1fr_1.1fr]">
            <div className="h-44">
              {dispatchBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dispatchBreakdown} dataKey="pieceCount" nameKey="label" innerRadius={42} outerRadius={70} paddingAngle={2}>
                      {dispatchBreakdown.map((item, index) => <Cell key={item.label} fill={dashboardGreens[index % dashboardGreens.length]} />)}
                    </Pie>
                    <ChartTooltip formatter={(value) => numberFormatter.format(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <EmptyMetric text="No completed dispatches today" />}
            </div>
            <div className="space-y-5">
              <div className="border-b border-[#c7ceb7] pb-4">
                <KpiBlock label="Today's dispatches" value={formatPeriodQuantity(dispatches?.daily)} />
              </div>
              <div className="space-y-1">
                {dispatchBreakdown.slice(0, 4).map((item, index) => (
                  <LegendDot key={item.label} color={dashboardGreens[index % dashboardGreens.length]} label={item.label} />
                ))}
              </div>
            </div>
          </div>
        </DashboardPanel>

        <DashboardPanel title="Daily Sowings" className="lg:col-span-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Completed sowing events today</p>
              <p className="mt-1 text-2xl font-bold text-[#276238]">{formatPeriodQuantity(sowings?.daily)}</p>
              <p className="mt-1 text-xs text-muted-foreground">{sowings?.daily.eventCount ?? 0} sowing batch event(s)</p>
            </div>
            <Sprout className="h-20 w-20 text-[#669442]" strokeWidth={1.2} />
          </div>
        </DashboardPanel>

        <DashboardPanel title="Gapping Done" className="lg:col-span-4">
          {gappings?.daily.availability === CompanyPerformanceMetricAvailability.NotTracked ? (
            <DataGapLine label="Not tracked" text={gappings.note ?? "Gapping is not recorded as a transaction."} />
          ) : <KpiBlock label="Gapping activity today" value={formatPeriodQuantity(gappings?.daily)} />}
        </DashboardPanel>
      </div>
      <footer className="border-t border-[#ccd2bc] px-4 py-2 text-[10px] text-muted-foreground">
        Piece quantities are converted to trays using the recorded tray capacity. Sales value is recognized from issued invoices by invoice date and is independent of cash collected. Dispatch cards count completed dispatch lines at the dispatch timestamp.
      </footer>
    </section>
  );
}

function DashboardPanel({ title, className = "", children }: { title: string; className?: string; children: React.ReactNode }) {
  return (
    <article className={`rounded-lg border border-[#c4cbb4] bg-[#fbfaf2] p-4 shadow-[0_2px_6px_rgba(45,65,34,.12)] ${className}`}>
      <h3 className="mb-4 border-b border-[#bfc7ae] pb-2 text-center text-base font-bold text-[#283126]">{title}</h3>
      {children}
    </article>
  );
}

function ExecutiveMetricCard({ label, value, period }: { label: string; value: string; period: string }) {
  return (
    <article className="rounded-lg border border-[#bdc7aa] bg-[#fbfaf2] px-4 py-3 shadow-sm">
      <p className="text-xs font-medium text-[#42603d]">{label}</p>
      <p className="mt-1 text-xl font-bold tabular-nums text-[#234f31]">{value}</p>
      <p className="mt-1 text-[10px] text-muted-foreground">{period}</p>
    </article>
  );
}

function SalesPeriodChart({ title, metrics, data, padded = false }: { title: string; metrics: SalesPerformanceReportDto["weekly"]; data: import("../services/directorsReportsApi").SalesSeriesPointDto[]; padded?: boolean }) {
  return (
    <div className={padded ? "sm:pl-3" : ""}>
      <p className="text-center text-base font-semibold text-[#35652d]">{title}</p>
      <p className="mt-1 text-center text-lg font-bold">{formatTrays(metrics.trayEquivalent)}</p>
      <p className="text-center text-sm font-semibold">{formatKsh(metrics.grossSalesAmount)}</p>
      <div className="mt-2 h-24">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <Bar dataKey="grossSalesAmount" fill="#5d923e" radius={[2, 2, 0, 0]} />
            <XAxis dataKey="date" hide />
            <YAxis hide />
            <ChartTooltip formatter={(value) => formatKsh(Number(value))} labelFormatter={(label) => String(label)} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function formatPeriodQuantity(period?: CompanyPerformancePeriodValueDto) {
  if (!period || period.availability === CompanyPerformanceMetricAvailability.NotTracked) return "Not tracked";
  return formatTrays(period.trayEquivalent ?? 0);
}

function formatTrays(trays: number) { return `${numberFormatter.format(trays)} Trays`; }

function compactMoney(value: number) { return value >= 1_000_000 ? `${(value / 1_000_000).toFixed(1)}m` : value >= 1_000 ? `${(value / 1_000).toFixed(0)}k` : String(value); }
function LegendDot({ color, label }: { color: string; label: string }) { return <span className="flex min-w-0 items-center gap-1.5"><i className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color }} /><span className="truncate">{label}</span></span>; }
function KpiLine({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) { return <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm">{label}</span><strong className={accent ? "text-lg text-[#2d6b35]" : "tabular-nums"}>{value}</strong></div>; }
function KpiBlock({ label, value }: { label: string; value: string }) { return <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-xl font-bold text-[#283b2a]">{value}</p></div>; }
function DataGapLine({ label, text }: { label: string; text: string }) { return <div className="mt-4 rounded-md border border-dashed border-amber-400/70 bg-amber-50/70 p-2 text-xs text-amber-950"><strong>{label}:</strong> {text}</div>; }
function EmptyMetric({ text }: { text: string }) { return <div className="flex h-full min-h-20 items-center justify-center text-center text-xs text-muted-foreground">{text}</div>; }

function CompanyPerformanceRow({ metric, currency }: { metric: CompanyPerformanceMetricDto; currency: string }) {
  const periods = [metric.daily, metric.weekly, metric.monthly, metric.yearToDate];
  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="flex flex-wrap items-center gap-2">
          <span>{metric.label}</span>
          {metric.yearToDate.availability === CompanyPerformanceMetricAvailability.Partial && (
            <Badge variant="outline" className="text-[10px]">Partial</Badge>
          )}
        </div>
      </TableCell>
      {periods.map((period, index) => (
        <TableCell key={`${metric.code}-${index}`} className="text-right align-top">
          <CompanyPerformancePeriodCell period={period} currency={currency} />
        </TableCell>
      ))}
      <TableCell className="whitespace-nowrap text-right align-top font-medium">
        <CompanyPerformanceTrend metric={metric} currency={currency} />
      </TableCell>
    </TableRow>
  );
}

function CompanyPerformanceTrend({ metric, currency }: { metric: CompanyPerformanceMetricDto; currency: string }) {
  const display = formatCompanyTrend(metric);
  const basis = metric.trendBasis === "MonetaryValue"
    ? "monetary value"
    : metric.trendBasis === "PhysicalQuantity" ? "physical quantity" : "untracked data";
  const formatValue = (value: number) => metric.trendBasis === "MonetaryValue"
    ? `${currency} ${moneyFormatter.format(value)}`
    : numberFormatter.format(value);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="cursor-help rounded-sm underline decoration-dotted underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Explain ${metric.label} monthly trend`}
        >
          {display}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs text-left leading-relaxed">
        <div className="font-semibold">{metric.label}</div>
        <div>Basis: {basis}.</div>
        {metric.trend.isComparable ? (
          <>
            <div>Current month-to-date: {formatValue(metric.trend.currentValue)}.</div>
            <div>Same elapsed period last month: {formatValue(metric.trend.previousValue)}.</div>
            <div>The percentage is calculated as (current − previous) ÷ previous × 100.</div>
          </>
        ) : (
          <div>No percentage is shown because the previous comparison value is zero or this metric is not tracked.</div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function CompanyPerformancePeriodCell({ period, currency }: { period: CompanyPerformancePeriodValueDto; currency: string }) {
  if (period.availability === CompanyPerformanceMetricAvailability.NotTracked) {
    return <span className="text-xs text-muted-foreground">Not tracked</span>;
  }
  const quantity = formatCompanyQuantity(period);
  const money = formatCompanyMoney(period, currency);
  return (
    <div className="space-y-1">
      {quantity !== "—" && <div className="text-xs font-medium tabular-nums">{quantity}</div>}
      {money !== "—" && <div className="text-xs tabular-nums text-muted-foreground">{money}</div>}
      {quantity === "—" && money === "—" && <span className="text-muted-foreground">—</span>}
    </div>
  );
}

function formatCompanyQuantity(period: CompanyPerformancePeriodValueDto) {
  if (period.availability === CompanyPerformanceMetricAvailability.NotTracked) return "Not tracked";
  if (period.pieceCount == null && period.trayEquivalent == null) return "—";
  return period.trayEquivalent == null ? "—" : formatTrays(period.trayEquivalent);
}

function formatCompanyMoney(period: CompanyPerformancePeriodValueDto, currency: string) {
  if (period.availability === CompanyPerformanceMetricAvailability.NotTracked) return "Not tracked";
  return period.monetaryValue == null ? "—" : `${currency} ${moneyFormatter.format(period.monetaryValue)}`;
}

function formatCompanyTrend(metric: CompanyPerformanceMetricDto) {
  if (!metric.trend.isComparable || metric.trend.percentageChange == null) return "—";
  const direction = metric.trend.direction;
  const arrow = direction === 0 || direction === "Up" ? "↑" : direction === 1 || direction === "Down" ? "↓" : "→";
  return `${arrow} ${Math.abs(metric.trend.percentageChange).toFixed(1)}%`;
}

const numberFormatter = new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 });
const moneyFormatter = new Intl.NumberFormat("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function SeedlingsMovementPanel({
  companyId,
  schedules,
  onChanged,
}: {
  companyId: string;
  schedules: DirectorsReportScheduleDto[];
  onChanged: () => void;
}) {
  const [from, setFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return format(date, "yyyy-MM-dd");
  });
  const [through, setThrough] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [recipientScheduleId, setRecipientScheduleId] = useState("");
  const actionLock = useRef(false);
  const reportQuery = useQuery({
    queryKey: ["seedlings-movement", companyId, from, through],
    queryFn: () => directorsReportsApi.getSeedlingsMovement(companyId, from, through),
    enabled: !!companyId && !!from && !!through && through >= from,
  });
  const exportReport = useMutation({
    mutationFn: async (action: "download" | "email") => {
      if (actionLock.current) return null;
      actionLock.current = true;
      try {
        const periodStart = new Date(`${from}T00:00:00Z`);
        const periodEnd = new Date(`${through}T00:00:00Z`);
        periodEnd.setUTCDate(periodEnd.getUTCDate() + 1);
        const generated = await directorsReportsApi.generate({
          companyId,
          reportType: DirectorsReportFrequency.Custom,
          periodStart: periodStart.toISOString(),
          periodEnd: periodEnd.toISOString(),
          enabledSections: [DirectorsReportSection.SeedlingsMovement],
          compareWithPreviousPeriod: false,
          deliverEmail: false,
          reportFrom: from,
          reportThrough: through,
        }, crypto.randomUUID());

        if (action === "download") {
          await directorsReportsApi.download(generated.id);
          return { action, message: "Seedlings movement audit PDF downloaded" };
        }
        const delivery = await directorsReportsApi.send(generated.id, recipientScheduleId);
        return { action, message: delivery.message, delivery };
      } finally {
        actionLock.current = false;
      }
    },
    onSuccess: (result) => {
      if (!result) return;
      if (result.action === "download") toast.success(result.message);
      else if (result.delivery?.status === "Delivered") toast.success(result.message);
      else if (result.delivery?.failedCount) toast.error(result.message);
      else toast.warning(result.message);
      onChanged();
    },
    onError: () => toast.error("The seedlings movement report could not be exported or sent."),
  });

  return (
    <Card>
      <CardHeader className="gap-4">
        <div>
          <CardTitle>Auditors Report — Overall Seedlings Movement</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Batch-level production, greenhouse, sales and destruction traceability. This report is manual-only and is never sent by an automated schedule.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[170px_170px_minmax(220px,1fr)_auto_auto]">
          <Field label="Sowed from" type="date" value={from} onChange={setFrom} />
          <Field label="Sowed through" type="date" value={through} onChange={setThrough} />
          <div className="grid gap-2">
            <Label>Manual email recipient group</Label>
            <Select value={recipientScheduleId} onValueChange={setRecipientScheduleId}>
              <SelectTrigger><SelectValue placeholder="Select a schedule" /></SelectTrigger>
              <SelectContent>
                {schedules.map((schedule) => (
                  <SelectItem key={schedule.id} value={schedule.id}>{schedule.scheduleName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            className="self-end"
            disabled={exportReport.isPending || !reportQuery.data}
            onClick={() => exportReport.mutate("download")}
          >
            <Download className="mr-2 h-4 w-4" />Export PDF
          </Button>
          <Button
            className="self-end"
            disabled={exportReport.isPending || !reportQuery.data || !recipientScheduleId}
            onClick={() => exportReport.mutate("email")}
          >
            <Send className="mr-2 h-4 w-4" />{exportReport.isPending ? "Working…" : "Email manually"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {through < from && <p className="text-sm text-destructive">The end date cannot be before the start date.</p>}
        {reportQuery.isLoading && <p className="py-8 text-center text-sm text-muted-foreground">Loading seedlings movement…</p>}
        {reportQuery.isError && (
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-destructive">
            Could not load the seedlings movement report.
            <Button variant="outline" size="sm" onClick={() => reportQuery.refetch()}>Retry</Button>
          </div>
        )}
        {reportQuery.data && <SeedlingsMovementTables report={reportQuery.data} />}
      </CardContent>
    </Card>
  );
}

function SeedlingsMovementTables({ report }: { report: SeedlingsMovementReportDto }) {
  return (
    <>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <span>Sowing dates: {report.from} to {report.through}</span>
        <span>Timezone: {report.businessTimezone}</span>
        <span>{report.rows.length} batch{report.rows.length === 1 ? "" : "es"}</span>
      </div>
      <AuditTable title="Production and greenhouse intake" rows={report.rows} mode="production" />
      <AuditTable title="Sales, greenhouse balance and destruction" rows={report.rows} mode="movement" />
      {report.rows.length === 0 && (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No sowing batches were recorded in this date range.
        </div>
      )}
      {report.dataQualityWarnings.length > 0 && (
        <div className="rounded-md border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-950">
          <p className="font-medium">Audit notes</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            {report.dataQualityWarnings.map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
        </div>
      )}
    </>
  );
}

function AuditTable({
  title,
  rows,
  mode,
}: {
  title: string;
  rows: SeedlingsMovementRowDto[];
  mode: "production" | "movement";
}) {
  const productionHeaders = ["Category", "Product", "Batch", "Date sowed", "Sowed", "Germinated", "To greenhouse", "Greenhouse / bay / bed", "Gapping used", "Available for sale"];
  const movementHeaders = ["Category", "Product", "Batch", "Sold", "Dispatched", "In greenhouse", "Unsold", "At-risk overgrown", "Ready destruction", "Destroyed", "Certificate"];
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="overflow-x-auto rounded-md border">
        <Table className={mode === "production" ? "min-w-[1250px]" : "min-w-[1150px]"}>
          <TableHeader>
            <TableRow className="bg-muted/70">
              {(mode === "production" ? productionHeaders : movementHeaders).map((header, index) => (
                <TableHead key={header} className={index > 2 ? "text-right" : ""}>{header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => mode === "production"
              ? <AuditProductionRow key={row.sowingInstructionId} row={row} />
              : <AuditMovementRow key={row.sowingInstructionId} row={row} />)}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function AuditIdentityCells({ row }: { row: SeedlingsMovementRowDto }) {
  return <><TableCell>{row.productCategory}</TableCell><TableCell>{row.productName}</TableCell><TableCell className="font-mono text-xs">{row.batchReference}</TableCell></>;
}

function AuditProductionRow({ row }: { row: SeedlingsMovementRowDto }) {
  return (
    <TableRow>
      <AuditIdentityCells row={row} />
      <TableCell className="text-right">{row.dateSown ?? "—"}</TableCell>
      <AuditNumber value={row.traysSown} /><AuditNumber value={row.traysGerminated} />
      <AuditNumber value={row.traysTransferredToGreenhouse} />
      <TableCell className="max-w-72 text-right text-xs">{row.greenhouseLocations || "Not assigned"}</TableCell>
      <TableCell className="text-right text-xs text-muted-foreground">Not tracked</TableCell>
      <AuditNumber value={row.traysAvailableForSale} />
    </TableRow>
  );
}

function AuditMovementRow({ row }: { row: SeedlingsMovementRowDto }) {
  return (
    <TableRow>
      <AuditIdentityCells row={row} />
      <AuditNumber value={row.traysSold} /><AuditNumber value={row.traysDispatched} />
      <AuditNumber value={row.traysInGreenhouse} /><AuditNumber value={row.traysInGreenhouseUnsold} />
      <AuditNumber value={row.traysAtRiskOvergrownUnsold} />
      <AuditNumber value={row.traysOvergrownReadyForDestruction} /><AuditNumber value={row.traysDestroyed} />
      <TableCell className="text-right text-xs text-muted-foreground">{row.destructionCertificateNumber ?? "Not tracked"}</TableCell>
    </TableRow>
  );
}

function AuditNumber({ value }: { value?: number | null }) {
  return <TableCell className="whitespace-nowrap text-right tabular-nums">{value == null ? "Unresolved" : numberFormatter.format(value)}</TableCell>;
}

function History({
  reports,
  schedules,
  onChanged,
}: {
  companyId: string;
  reports: DirectorsReportSummaryDto[];
  schedules: DirectorsReportScheduleDto[];
  onChanged: () => void;
}) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [manualRecipientScheduleId, setManualRecipientScheduleId] = useState("");
  const defaultRecipientScheduleId = schedules.find((schedule) =>
    schedule.scheduleName.toLowerCase().includes("daily directors"))?.id ?? schedules[0]?.id ?? "";
  const effectiveManualRecipientScheduleId = manualRecipientScheduleId || defaultRecipientScheduleId;
  const detailQuery = useQuery({
    queryKey: ["directors-report-detail", selectedReportId],
    queryFn: () => directorsReportsApi.getReport(selectedReportId!),
    enabled: !!selectedReportId,
  });

  const send = useMutation({
    mutationFn: ({ reportId, recipientScheduleId }: { reportId: string; recipientScheduleId?: string }) =>
      directorsReportsApi.send(reportId, recipientScheduleId),
    onSuccess: (result) => {
      if (result.status === "Delivered") toast.success(result.message);
      else if (result.status === "Skipped") toast.warning(result.message);
      else if (result.failedCount > 0) toast.error(result.message);
      else toast.info(result.message);
      onChanged();
      detailQuery.refetch();
    },
    onError: () => toast.error("Could not send the report. Check recipients and delivery settings."),
  });

  const sendReport = (report: DirectorsReportSummaryDto | DirectorsReportDetailDto) => {
    const recipientScheduleId = report.generationSource === "Manual" ? effectiveManualRecipientScheduleId : undefined;
    if (report.generationSource === "Manual" && !recipientScheduleId) {
      toast.error("Select a recipient group before sending a manually generated report.");
      return;
    }
    send.mutate({ reportId: report.id, recipientScheduleId });
  };

  const download = async (reportId: string) => {
    try {
      setDownloadingId(reportId);
      await directorsReportsApi.download(reportId);
      toast.success("Report download started");
    } catch (error) {
      toast.error(await downloadErrorMessage(error));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <CardTitle>Report History</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">Choose a recipient group before sending a manual report.</p>
        </div>
        <div className="w-full space-y-1.5 sm:max-w-xs">
          <Label>Manual report recipient group</Label>
          <Select value={effectiveManualRecipientScheduleId} onValueChange={setManualRecipientScheduleId}>
            <SelectTrigger><SelectValue placeholder="Select a schedule" /></SelectTrigger>
            <SelectContent>
              {schedules.map((schedule) => (
                <SelectItem key={schedule.id} value={schedule.id}>{schedule.scheduleName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Report</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Generated</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Generation</TableHead>
              <TableHead>Delivery</TableHead>
              <TableHead>Recipients</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.reportName}</TableCell>
                <TableCell>{frequencyLabel(r.reportType)}</TableCell>
                <TableCell>{formatPeriod(r)}</TableCell>
                <TableCell>{r.generatedAt ? format(new Date(r.generatedAt), "dd MMM yyyy HH:mm") : "-"}</TableCell>
                <TableCell>{r.generationSource}</TableCell>
                <TableCell><Badge variant={r.generationStatus === "Failed" ? "destructive" : "secondary"}>{r.generationStatus}</Badge></TableCell>
                <TableCell>
                  <div className="grid gap-1">
                    <Badge variant={r.deliveryStatus === "Delivered" ? "default" : r.deliveryStatus === "Failed" || r.deliveryStatus === "PartiallyDelivered" ? "destructive" : "secondary"}>
                      {deliveryStatusLabel(r)}
                    </Badge>
                    {r.lastDeliveryFailureReason && <span className="max-w-60 truncate text-xs text-muted-foreground" title={r.lastDeliveryFailureReason}>{r.lastDeliveryFailureReason}</span>}
                  </div>
                </TableCell>
                <TableCell>{r.recipientCount ? `${r.deliveredCount}/${r.recipientCount}` : "-"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => setSelectedReportId(r.id)} aria-label="View report details" title="View report details">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => download(r.id)} disabled={!canUseReportActions(r) || downloadingId === r.id} aria-label="Download report" title="Download report">
                    <Download className={`h-4 w-4 ${downloadingId === r.id ? "animate-spin" : ""}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (r.deliveryStatus === "Delivered" && !window.confirm(`This report has already been delivered to ${r.deliveredCount} recipients. Send it again?`)) return;
                      sendReport(r);
                    }}
                    disabled={send.isPending || !canUseReportActions(r)}
                    aria-label="Send report"
                    title={send.isPending ? "Queueing..." : "Send report"}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {reports.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">No reports generated</TableCell></TableRow>}
          </TableBody>
        </Table>
        <ReportDetails
          report={detailQuery.data ?? null}
          open={!!selectedReportId}
          onOpenChange={(open) => !open && setSelectedReportId(null)}
          onDownload={download}
          onSend={sendReport}
        />
      </CardContent>
    </Card>
  );
}

function ReportDetails({
  report,
  open,
  onOpenChange,
  onDownload,
  onSend,
}: {
  report: DirectorsReportDetailDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload: (id: string) => void;
  onSend: (report: DirectorsReportDetailDto) => void;
}) {
  const performance = parseSalesPerformance(report?.metricSnapshotJson);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{report?.reportName ?? "Report details"}</SheetTitle>
        </SheetHeader>
        {report && (
          <div className="mt-6 grid gap-6">
            <div className="grid gap-3 rounded-md border p-4 text-sm sm:grid-cols-2">
              <Detail label="Type" value={frequencyLabel(report.reportType)} />
              <Detail label="Period" value={formatPeriod(report)} />
              <Detail label="Source" value={report.generationSource} />
              <Detail label="Generated" value={report.generatedAt ? format(new Date(report.generatedAt), "dd MMM yyyy HH:mm") : "-"} />
              <Detail label="Generation" value={report.generationStatus} />
              <Detail label="Delivery" value={deliveryStatusLabel(report)} />
              <Detail label="File" value={report.hasFile ? `${report.fileName} (${formatBytes(report.fileSizeBytes)})` : "File missing"} />
              <Detail label="Last attempt" value={report.lastDeliveryAttemptAt ? format(new Date(report.lastDeliveryAttemptAt), "dd MMM yyyy HH:mm") : "-"} />
              {report.lastDeliveryFailureReason && <Detail label="Failure reason" value={report.lastDeliveryFailureReason} />}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onDownload(report.id)} disabled={!canUseReportActions(report)}>
                <Download className="mr-2 h-4 w-4" />Download
              </Button>
              <Button onClick={() => onSend(report)} disabled={!canUseReportActions(report)}>
                <Send className="mr-2 h-4 w-4" />Send report
              </Button>
            </div>

            {performance && <SalesPerformanceTable performance={performance} />}

            <div className="grid gap-2">
              <h3 className="text-sm font-medium">Delivery attempts</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attempted</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.deliveries.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>
                        <div className="grid">
                          <span>{d.recipientName}</span>
                          <span className="text-xs text-muted-foreground">{d.recipientEmail}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant={d.deliveryStatus === 2 ? "default" : d.deliveryStatus === 3 || d.deliveryStatus === 4 ? "destructive" : "secondary"}>{deliveryAttemptLabel(d.deliveryStatus)}</Badge></TableCell>
                      <TableCell>{format(new Date(d.attemptedAt), "dd MMM yyyy HH:mm")}</TableCell>
                      <TableCell className="max-w-64 truncate" title={d.failureReason ?? undefined}>{d.failureReason ?? "-"}</TableCell>
                    </TableRow>
                  ))}
                  {report.deliveries.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">No delivery attempts yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SalesPerformanceTable({ performance }: { performance: SalesPerformanceReportDto }) {
  const periods = [performance.daily, performance.weekly, performance.monthly, performance.yearToDate];
  const trend = performance.trend.monthly;
  return (
    <div className="grid gap-2">
      <div>
        <h3 className="text-sm font-medium">Sales performance</h3>
        <p className="text-xs text-muted-foreground">Gross eligible orders through {performance.asOf} ({performance.businessTimezone})</p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Metric</TableHead>
            <TableHead>{performance.periodLabels.daily}</TableHead>
            <TableHead>{performance.periodLabels.weekly}</TableHead>
            <TableHead>{performance.periodLabels.monthly}</TableHead>
            <TableHead>{performance.periodLabels.yearToDate}</TableHead>
            <TableHead>Trend · {performance.periodLabels.monthlyTrend}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="font-medium">Sales value</TableCell>
            {periods.map((period, index) => <TableCell key={index}>{formatKsh(period.grossSalesAmount)}</TableCell>)}
            <TableCell>{formatSalesTrend(trend)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="font-medium">No. of Trays</TableCell>
            {periods.map((period, index) => <TableCell key={index}>{formatTrays(period.trayEquivalent)}</TableCell>)}
            <TableCell>{formatSalesTrend(performance.trayTrend.monthly)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {performance.dataQuality.unresolvedLineCount > 0 && (
        <p className="text-xs text-amber-700">
          {performance.dataQuality.unresolvedLineCount} historical line(s) could not be safely normalized; quantity totals exclude unresolved values.
        </p>
      )}
    </div>
  );
}

function parseSalesPerformance(snapshotJson?: string | null): SalesPerformanceReportDto | null {
  if (!snapshotJson) return null;
  try {
    const snapshot = JSON.parse(snapshotJson) as { sales?: { performance?: SalesPerformanceReportDto } };
    return snapshot.sales?.performance ?? null;
  } catch {
    return null;
  }
}

const formatKsh = (value: number) => `KSh ${new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 }).format(value)}`;
const formatSalesTrend = (trend: SalesPerformanceReportDto["trend"]["monthly"]) =>
  trend.isComparable && trend.percentageChange != null
    ? `${salesTrendDirectionLabel(trend.direction)} ${Math.abs(trend.percentageChange).toFixed(2)}%`
    : "Not comparable";
const salesTrendDirectionLabel = (direction: number | string) =>
  typeof direction === "number" ? (["Up", "Down", "Stable", "Not comparable"][direction] ?? "Stable") : direction;

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="break-words">{value}</span>
    </div>
  );
}

function SettingsPanel({ companyId, settings, schedules, onChanged }: { companyId: string; settings: DirectorsReportSettingsDto; schedules: DirectorsReportScheduleDto[]; onChanged: () => void }) {
  const [draft, setDraft] = useState(settings);
  const [selectedScheduleId, setSelectedScheduleId] = useState(schedules[0]?.id ?? "");
  const selected = schedules.find((s) => s.id === selectedScheduleId) ?? schedules[0];
  const [schedule, setSchedule] = useState<DirectorsReportScheduleDto | null>(selected ?? null);
  const preview = useMemo(() => buildBrandingPreview(draft, settings.brandingPreview), [draft, settings.brandingPreview]);

  const saveSettings = useMutation({
    mutationFn: () => directorsReportsApi.updateSettings(companyId, {
      automatedReportingEnabled: draft.automatedReportingEnabled,
      businessTimezone: draft.businessTimezone,
      currency: draft.currency,
      financialYearStartMonth: Number(draft.financialYearStartMonth),
      workingDays: draft.workingDays,
      defaultReportFormat: draft.defaultReportFormat,
      reportRetentionDays: Number(draft.reportRetentionDays),
      reportDisplayNameOverride: draft.reportDisplayNameOverride,
      emailSenderDisplayName: draft.emailSenderDisplayName,
      reportTitleTemplate: draft.reportTitleTemplate,
      emailSubjectTemplate: draft.emailSubjectTemplate,
      emailHeadingTemplate: draft.emailHeadingTemplate,
      fileNameTemplate: draft.fileNameTemplate,
      includeCompanyLogo: draft.includeCompanyLogo,
      dateFormat: draft.dateFormat,
    }),
    onSuccess: () => { toast.success("Settings saved"); onChanged(); },
  });

  const saveSchedule = useMutation({
    mutationFn: () => directorsReportsApi.saveSchedule(companyId, {
      scheduleName: schedule!.scheduleName,
      frequency: Number(schedule!.frequency) as DirectorsReportFrequency,
      isEnabled: schedule!.isEnabled,
      deliveryTime: schedule!.deliveryTime,
      deliveryDay: schedule!.deliveryDay,
      reportingPeriodRule: Number(schedule!.reportingPeriodRule) as DirectorsReportPeriodRule,
      compareWithPreviousPeriod: schedule!.compareWithPreviousPeriod,
      attachPdf: schedule!.attachPdf,
      includeEmailSummary: schedule!.includeEmailSummary,
      enabledSections: sanitizeSections(schedule!.enabledSections),
    }, schedule!.id),
    onSuccess: () => { toast.success("Schedule saved"); onChanged(); },
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
      <Card>
        <CardHeader><CardTitle>General Settings</CardTitle></CardHeader>
        <CardContent className="grid gap-4">
          <ToggleRow label="Automated reporting" checked={draft.automatedReportingEnabled} onChange={(v) => setDraft({ ...draft, automatedReportingEnabled: v })} />
          <Field label="Business timezone" value={draft.businessTimezone} onChange={(v) => setDraft({ ...draft, businessTimezone: v })} />
          <Field label="Currency" value={draft.currency} onChange={(v) => setDraft({ ...draft, currency: v.toUpperCase() })} />
          <Field label="Financial year start month" type="number" value={String(draft.financialYearStartMonth)} onChange={(v) => setDraft({ ...draft, financialYearStartMonth: Number(v) })} />
          <Field label="Retention days" type="number" value={String(draft.reportRetentionDays)} onChange={(v) => setDraft({ ...draft, reportRetentionDays: Number(v) })} />
          <div className="grid gap-2">
            <Label>Working days</Label>
            <div className="grid grid-cols-2 gap-2">
              {dayOptions.map(([value, label]) => (
                <ToggleRow key={value} label={label} checked={draft.workingDays.includes(value)} onChange={(checked) => {
                  const days = checked ? [...draft.workingDays, value] : draft.workingDays.filter((d) => d !== value);
                  setDraft({ ...draft, workingDays: days });
                }} />
              ))}
            </div>
          </div>
          <Button onClick={() => saveSettings.mutate()}>Save Settings</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Branding</CardTitle></CardHeader>
        <CardContent className="grid gap-4">
          <Field label="Report display name override" value={draft.reportDisplayNameOverride ?? ""} onChange={(v) => setDraft({ ...draft, reportDisplayNameOverride: v })} />
          <Field label="Email sender display name" value={draft.emailSenderDisplayName ?? ""} onChange={(v) => setDraft({ ...draft, emailSenderDisplayName: v })} />
          <Field label="Report title template" value={draft.reportTitleTemplate} onChange={(v) => setDraft({ ...draft, reportTitleTemplate: v })} />
          <Field label="Email subject template" value={draft.emailSubjectTemplate} onChange={(v) => setDraft({ ...draft, emailSubjectTemplate: v })} />
          <Field label="Email heading template" value={draft.emailHeadingTemplate} onChange={(v) => setDraft({ ...draft, emailHeadingTemplate: v })} />
          <Field label="File name template" value={draft.fileNameTemplate} onChange={(v) => setDraft({ ...draft, fileNameTemplate: v })} />
          <Field label="Date format" value={draft.dateFormat} onChange={(v) => setDraft({ ...draft, dateFormat: v })} />
          <ToggleRow label="Include company logo" checked={draft.includeCompanyLogo} onChange={(v) => setDraft({ ...draft, includeCompanyLogo: v })} />
          <div className="grid gap-2 rounded-md border bg-muted/30 p-3 text-sm">
            <div><span className="text-muted-foreground">Email subject:</span> {preview.emailSubject}</div>
            <div><span className="text-muted-foreground">PDF heading:</span> {preview.pdfHeading}</div>
            <div><span className="text-muted-foreground">Attachment:</span> {preview.attachmentFileName}</div>
          </div>
          <Button onClick={() => saveSettings.mutate()}>Save Branding</Button>
        </CardContent>
      </Card>

      {schedule && (
        <Card>
          <CardHeader><CardTitle>Schedules</CardTitle></CardHeader>
          <CardContent className="grid gap-4">
            <Select value={schedule.id} onValueChange={(id) => {
              const next = schedules.find((s) => s.id === id) ?? schedule;
              setSelectedScheduleId(id);
              setSchedule(next);
            }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{schedules.map((s) => <SelectItem key={s.id} value={s.id}>{s.scheduleName}</SelectItem>)}</SelectContent>
            </Select>
            <Field label="Schedule name" value={schedule.scheduleName} onChange={(v) => setSchedule({ ...schedule, scheduleName: v })} />
            <ToggleRow label="Enabled" checked={schedule.isEnabled} onChange={(v) => setSchedule({ ...schedule, isEnabled: v })} />
            <Field label="Delivery time" value={schedule.deliveryTime} onChange={(v) => setSchedule({ ...schedule, deliveryTime: v })} />
            <Select value={String(schedule.frequency)} onValueChange={(v) => setSchedule({ ...schedule, frequency: Number(v) as DirectorsReportFrequency })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Daily</SelectItem>
                <SelectItem value="2">Weekly</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(schedule.deliveryDay ?? 1)} onValueChange={(v) => setSchedule({ ...schedule, deliveryDay: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{dayOptions.map(([value, label]) => <SelectItem key={value} value={String(value)}>{label}</SelectItem>)}</SelectContent>
            </Select>
            <ToggleRow label="Compare with previous period" checked={schedule.compareWithPreviousPeriod} onChange={(v) => setSchedule({ ...schedule, compareWithPreviousPeriod: v })} />
            <ToggleRow label="Attach PDF" checked={schedule.attachPdf} onChange={(v) => setSchedule({ ...schedule, attachPdf: v })} />
            <ToggleRow label="Include email summary" checked={schedule.includeEmailSummary} onChange={(v) => setSchedule({ ...schedule, includeEmailSummary: v })} />
            <SectionToggles value={schedule.enabledSections} onChange={(enabledSections) => setSchedule({ ...schedule, enabledSections })} />
            <Button onClick={() => saveSchedule.mutate()} disabled={sanitizeSections(schedule.enabledSections).length === 0}>Save Schedule</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RecipientsPanel({ companyId, schedules, recipients, onChanged }: { companyId: string; schedules: DirectorsReportScheduleDto[]; recipients: DirectorsReportRecipientDto[]; onChanged: () => void }) {
  const blank = useMemo(() => ({
    name: "",
    emailAddress: "",
    isActive: true,
    deliveryType: DirectorsReportDeliveryType.To,
    receivePdf: true,
    receiveEmailSummary: true,
    scheduleIds: schedules.map((s) => s.id),
  }), [schedules]);
  const [draft, setDraft] = useState<Omit<DirectorsReportRecipientDto, "id" | "companyId" | "lastSuccessfulDeliveryAt" | "lastFailedDeliveryReason">>(blank);
  const save = useMutation({
    mutationFn: async () => {
      const emails = splitRecipientEmails(draft.emailAddress);
      if (emails.length === 0) throw new Error("Enter at least one recipient email address.");

      const invalidEmails = emails.filter((email) => !isValidRecipientEmail(email));
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid email address${invalidEmails.length > 1 ? "es" : ""}: ${invalidEmails.join(", ")}`);
      }

      const baseName = draft.name.trim();
      const payloads = emails.map((email) => ({
        ...draft,
        name: (baseName || email.split("@")[0]).slice(0, 200),
        emailAddress: email,
      }));

      const results = await Promise.allSettled(payloads.map((payload) => directorsReportsApi.saveRecipient(companyId, payload)));
      const saved = results.filter((result) => result.status === "fulfilled").length;
      const errors = results
        .filter((result): result is PromiseRejectedResult => result.status === "rejected")
        .map((result) => (result.reason instanceof Error ? result.reason.message : "Could not save recipient."));

      if (saved === 0) throw new Error(errors[0] ?? "Could not save recipients.");
      return { attempted: emails.length, saved, failed: emails.length - saved, errors };
    },
    onSuccess: (result) => {
      if (result.failed === 0) {
        toast.success(result.saved === 1 ? "Recipient saved" : `${result.saved} recipients saved`);
      } else {
        toast.warning(`${result.saved} of ${result.attempted} recipients were saved. ${result.failed} failed.`);
        if (result.errors[0]) toast.error(result.errors[0]);
      }
      setDraft(blank);
      onChanged();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Could not save recipient."),
  });
  const remove = useMutation({
    mutationFn: (recipientId: string) => directorsReportsApi.deleteRecipient(companyId, recipientId),
    onSuccess: () => {
      toast.success("Recipient removed");
      onChanged();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Could not remove recipient."),
  });
  const removeRecipient = (recipient: DirectorsReportRecipientDto) => {
    if (!window.confirm(`Remove ${recipient.name} (${recipient.emailAddress}) from report recipients?`)) return;
    remove.mutate(recipient.id);
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
      <Card>
        <CardHeader><CardTitle>Add Recipient</CardTitle></CardHeader>
        <CardContent className="grid gap-4">
          <Field label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
          <Field label="Email" value={draft.emailAddress} onChange={(v) => setDraft({ ...draft, emailAddress: v })} />
          <p className="text-xs text-muted-foreground">Enter multiple emails separated by commas.</p>
          <ToggleRow label="Active" checked={draft.isActive} onChange={(v) => setDraft({ ...draft, isActive: v })} />
          <ToggleRow label="Receive PDF" checked={draft.receivePdf} onChange={(v) => setDraft({ ...draft, receivePdf: v })} />
          <ToggleRow label="Receive summary" checked={draft.receiveEmailSummary} onChange={(v) => setDraft({ ...draft, receiveEmailSummary: v })} />
          <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? "Saving..." : "Save Recipient"}</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Recipients</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead>Schedules</TableHead><TableHead className="w-12"><span className="sr-only">Actions</span></TableHead></TableRow></TableHeader>
            <TableBody>
              {recipients.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.emailAddress}</TableCell>
                  <TableCell><Badge variant={r.isActive ? "default" : "secondary"}>{r.isActive ? "Active" : "Inactive"}</Badge></TableCell>
                  <TableCell>{r.scheduleIds.length}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      aria-label={`Remove ${r.name}`}
                      title={`Remove ${r.name}`}
                      disabled={remove.isPending}
                      onClick={() => removeRecipient(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function GeneratePanel({
  companyId,
  businessTimezone,
  schedules,
  onChanged,
}: {
  companyId: string;
  businessTimezone: string;
  schedules: DirectorsReportScheduleDto[];
  onChanged: () => void;
}) {
  const today = getBusinessDateToday(businessTimezone);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [sections, setSections] = useState<DirectorsReportSection[]>(sectionOptions.map(([v]) => v));
  const [recipientScheduleId, setRecipientScheduleId] = useState("");
  const defaultRecipientScheduleId = schedules.find((schedule) =>
    schedule.scheduleName.toLowerCase().includes("daily directors"))?.id ?? schedules[0]?.id ?? "";
  const effectiveRecipientScheduleId = recipientScheduleId || defaultRecipientScheduleId;
  const submissionLock = useRef(false);
  const idempotencyAttempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const generate = useMutation({
    mutationFn: async ({ idempotencyKey, email }: { idempotencyKey: string; email: boolean }) => {
      const periodEnd = new Date(`${to}T00:00:00Z`);
      periodEnd.setUTCDate(periodEnd.getUTCDate() + 1);
      const generated = await directorsReportsApi.generate({
        companyId,
        reportType: DirectorsReportFrequency.Custom,
        periodStart: new Date(`${from}T00:00:00Z`).toISOString(),
        periodEnd: periodEnd.toISOString(),
        enabledSections: sanitizeSections(sections),
        compareWithPreviousPeriod: true,
        deliverEmail: false,
        asOf: to,
        reportFrom: from,
        reportThrough: to,
      }, idempotencyKey);
      const delivery = email
        ? await directorsReportsApi.send(generated.id, effectiveRecipientScheduleId)
        : null;
      return { generated, delivery };
    },
    onSuccess: ({ delivery }) => {
      idempotencyAttempt.current = null;
      if (!delivery) toast.success("Report generated");
      else if (delivery.status === "Delivered") toast.success(delivery.message);
      else if (delivery.failedCount) toast.error(delivery.message);
      else toast.warning(delivery.message);
      onChanged();
    },
    onError: () => toast.error("The report could not be generated."),
    onSettled: () => {
      submissionLock.current = false;
      setIsSubmitting(false);
    },
  });

  const submit = (email: boolean) => {
    if (submissionLock.current) return;
    if (from > to) {
      toast.error("The From date must be on or before the To date.");
      return;
    }
    if (to > today) {
      toast.error(`The To date cannot be later than ${today}.`);
      return;
    }
    if (email && !effectiveRecipientScheduleId) {
      toast.error("Select a recipient group before generating and emailing the report.");
      return;
    }
    const fingerprint = JSON.stringify({
      from,
      to,
      sections: sanitizeSections(sections).sort((a, b) => a - b),
      email,
      recipientScheduleId: email ? effectiveRecipientScheduleId : null,
    });
    if (idempotencyAttempt.current?.fingerprint !== fingerprint) {
      idempotencyAttempt.current = { fingerprint, key: crypto.randomUUID() };
    }
    submissionLock.current = true;
    setIsSubmitting(true);
    generate.mutate({ idempotencyKey: idempotencyAttempt.current.key, email });
  };
  return (
    <Card>
      <CardHeader><CardTitle>Manual Custom Date Range</CardTitle></CardHeader>
      <CardContent className="grid max-w-xl gap-4">
        <Field label="From" type="date" value={from} max={today} onChange={setFrom} />
        <Field label="To" type="date" value={to} min={from} max={today} onChange={setTo} />
        <SectionToggles value={sections} onChange={setSections} />
        <div className="space-y-1.5">
          <Label>Email recipient group</Label>
          <Select value={effectiveRecipientScheduleId} onValueChange={setRecipientScheduleId}>
            <SelectTrigger><SelectValue placeholder="Select a schedule to enable email" /></SelectTrigger>
            <SelectContent>
              {schedules.map((schedule) => (
                <SelectItem key={schedule.id} value={schedule.id}>{schedule.scheduleName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Button
            variant="outline"
            onClick={() => submit(false)}
            disabled={isSubmitting || generate.isPending || sanitizeSections(sections).length === 0}
          >
            {isSubmitting || generate.isPending ? "Generating…" : "Generate PDF"}
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={isSubmitting || generate.isPending || sanitizeSections(sections).length === 0 || !effectiveRecipientScheduleId}
          >
            <Send className="mr-2 h-4 w-4" />{isSubmitting || generate.isPending ? "Working…" : "Generate & Email"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionToggles({ value, onChange }: { value: DirectorsReportSection[]; onChange: (value: DirectorsReportSection[]) => void }) {
  const sanitizedValue = sanitizeSections(value);
  return (
    <div className="grid gap-2">
      <Label>Report content</Label>
      <p className="text-xs text-muted-foreground">
        Sowing &amp; Operations is added as its own page in a manual PDF; scheduled runs send it as a separate attachment.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {sectionOptions.map(([section, label]) => (
          <ToggleRow key={section} label={label} checked={sanitizedValue.includes(section)} onChange={(checked) => {
            onChange(checked ? [...sanitizedValue, section] : sanitizedValue.filter((x) => x !== section));
          }} />
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", min, max }: { label: string; value: string; onChange: (value: string) => void; type?: string; min?: string; max?: string }) {
  return <div className="grid gap-2"><Label>{label}</Label><Input type={type} value={value} min={min} max={max} onChange={(e) => onChange(e.target.value)} /></div>;
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <span>{label}</span>
    </label>
  );
}

function frequencyLabel(value: number) {
  return ({ 1: "Daily", 2: "Weekly", 3: "Monthly", 4: "Quarterly", 5: "Semi-annual", 6: "Annual", 7: "Custom" } as Record<number, string>)[value] ?? "Report";
}

async function downloadErrorMessage(error: unknown) {
  const fallback = "Could not download the report.";
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    const text = await data.text();
    try {
      const body = JSON.parse(text) as { userMessage?: string; message?: string; nextStep?: string };
      return [body.userMessage ?? body.message, body.nextStep].filter(Boolean).join(" ");
    } catch {
      return text || fallback;
    }
  }
  return fallback;
}

function canUseReportActions(report: Pick<DirectorsReportSummaryDto, "generationStatus" | "status">) {
  if (report.generationStatus) return report.generationStatus === "Generated";
  return report.status === 2 || report.status === 3 || report.status === 4;
}

function formatPeriod(report: Pick<DirectorsReportSummaryDto, "reportType" | "periodStart" | "periodEnd" | "periodEndIsExclusive" | "periodLabel">) {
  if (report.periodLabel) return report.periodLabel;
  const start = new Date(report.periodStart);
  const end = new Date(report.periodEnd);
  const displayEnd = report.periodEndIsExclusive ? new Date(end.getTime() - 1) : end;
  if (report.reportType === DirectorsReportFrequency.Daily || format(start, "yyyy-MM-dd") === format(displayEnd, "yyyy-MM-dd")) {
    return format(start, "dd MMM yyyy");
  }
  return `${format(start, "dd MMM yyyy")} - ${format(displayEnd, "dd MMM yyyy")}`;
}

function buildBrandingPreview(settings: DirectorsReportSettingsDto, base: DirectorsReportSettingsDto["brandingPreview"]) {
  const companyName = (settings.reportDisplayNameOverride || base.companyName || "FarmSmart ERP").trim();
  const values: Record<string, string> = {
    CompanyName: companyName,
    LegalName: base.legalName,
    TradingName: base.tradingName,
    Frequency: "Weekly",
    PeriodLabel: base.periodLabel,
    PeriodStart: "2026-07-05",
    PeriodEnd: "2026-07-11",
    GeneratedDate: format(new Date(), "yyyy-MM-dd"),
    Currency: settings.currency,
    CompanySlug: slugify(companyName),
    FrequencySlug: "weekly",
  };
  const render = (template: string) => template.replace(/\{([A-Za-z][A-Za-z0-9]*)\}/g, (match, key) => values[key] ?? match);
  const fileName = sanitizeFileName(render(settings.fileNameTemplate || "{FrequencySlug}-{CompanySlug}-directors-report-{PeriodStart}-{PeriodEnd}.pdf"));

  return {
    emailSubject: render(settings.emailSubjectTemplate || "{CompanyName} {Frequency} Directors Report – {PeriodLabel}"),
    pdfHeading: render(settings.reportTitleTemplate || "{CompanyName} Directors Management Report"),
    attachmentFileName: fileName,
  };
}

function slugify(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "company";
}

function sanitizeFileName(value: string) {
  const clean = value.replace(/[\\/:*?"<>|]+/g, "-").replace(/-{2,}/g, "-").replace(/^[.\-\s]+|[.\-\s]+$/g, "").toLowerCase();
  return clean.endsWith(".pdf") ? clean : `${clean || "directors-report"}.pdf`;
}

function deliveryStatusLabel(report: Pick<DirectorsReportSummaryDto, "deliveryStatus" | "recipientCount" | "deliveredCount" | "failedCount" | "pendingCount">) {
  if (report.deliveryStatus === "Delivered") return `Delivered to ${report.deliveredCount} of ${report.recipientCount}`;
  if (report.deliveryStatus === "PartiallyDelivered") return `Delivered to ${report.deliveredCount} of ${report.recipientCount}`;
  if (report.deliveryStatus === "Failed") return `Failed for ${report.failedCount} of ${report.recipientCount}`;
  if (report.deliveryStatus === "Pending") return `Pending for ${report.pendingCount} of ${report.recipientCount}`;
  return "Not sent";
}

function deliveryAttemptLabel(status: number) {
  return ({ 1: "Pending", 2: "Sent", 3: "Failed", 4: "Skipped" } as Record<number, string>)[status] ?? "Unknown";
}

function formatBytes(value?: number | null) {
  if (!value) return "0 B";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}
